import { useEffect, useRef } from 'react';
import { palette } from '../pricing/pricingTokens';

/**
 * A small popover anchored to whatever opened it.
 *
 * Deliberately not an expanding panel. The row it belongs to is two cards
 * wide, and pushing content down to make room moves the price ladder under the
 * user's thumb mid-tap. A popover leaves the page where it was, and closing it
 * puts everything back exactly as it was found.
 *
 * Closes on a tap outside, on Escape, and on choosing something. It never
 * blocks the screen behind it with a full-strength scrim, because it is a menu
 * rather than a decision.
 */
export default function ChasePopover({ anchorRef, open, onClose, theme, isDark = true, children }) {
  const c = palette(theme, isDark);
  const s = styles(c);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Anchored to the card that opened it, in viewport coordinates, so a page
  // that scrolls behind the popover cannot leave it pointing at nothing: the
  // scrim swallows the scroll.
  const rect = anchorRef?.current?.getBoundingClientRect();
  const width = 224;
  const gap = 8;

  const left = rect
    ? Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 8)
    : 8;

  // Below the anchor when there is room, above it when there is not, so the
  // last card on a short screen still shows its whole menu.
  const spaceBelow = rect ? window.innerHeight - rect.bottom : 0;
  const dropUp = rect && spaceBelow < 220;
  const top = rect ? (dropUp ? undefined : rect.bottom + gap) : 80;
  const bottom = dropUp ? window.innerHeight - rect.top + gap : undefined;

  return (
    <div style={s.scrim} onClick={onClose} role="presentation">
      <div
        ref={boxRef}
        role="menu"
        style={{ ...s.box, width: `${width}px`, left: `${left}px`, top: top ? `${top}px` : undefined, bottom: bottom ? `${bottom}px` : undefined, transformOrigin: dropUp ? 'bottom center' : 'top center' }}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          style={{
            ...s.beak,
            left: rect ? `${Math.min(Math.max(12, rect.left + rect.width / 2 - left - 5), width - 22)}px` : '50%',
            ...(dropUp ? { bottom: '-5px' } : { top: '-5px' }),
          }}
        />
        {children}
      </div>
    </div>
  );
}

const styles = (c) => ({
  scrim: {
    position: 'fixed', inset: 0, zIndex: 880,
    // Barely there. Enough to catch the tap and to lift the popover off the
    // page, not enough to read as a modal.
    background: 'rgba(0, 0, 0, 0.32)',
    animation: 'cc-fade 0.16s ease both',
  },
  box: {
    position: 'fixed', zIndex: 881,
    background: c.popover, border: `0.5px solid ${c.chipBorder}`, borderRadius: '14px',
    padding: '6px', boxShadow: '0 18px 40px -12px rgba(0, 0, 0, 0.6)',
    animation: 'cc-pop 0.18s cubic-bezier(0.2, 0.8, 0.3, 1) both',
  },
  beak: {
    position: 'absolute', width: '10px', height: '10px', background: c.popover,
    borderLeft: `0.5px solid ${c.chipBorder}`, borderTop: `0.5px solid ${c.chipBorder}`,
    transform: 'rotate(45deg)', borderRadius: '2px',
  },
});
